import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReviewReminderFormType } from '../types';
import { Reminder } from '@/common';
import { getBeautifiedLocalDate } from '@/core';

type ReviewReminderModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: (data: ReviewReminderFormType) => void;
  reminder?: Reminder;
};

const ReviewReminderModal = ({
  isOpen,
  setIsOpen,
  onSubmit,
  reminder,
}: ReviewReminderModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const form = useForm<ReviewReminderFormType>({
    mode: 'onTouched',
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset({
        endType: '',
      });
    }
  }, [isOpen, form]);

  const baseSubmit = (data: ReviewReminderFormType) => {
    onSubmit(data);
  };

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle id="responsive-dialog-title">
        {t('Reminder', { defaultValue: 'Reminder' })} {reminder?.name || 'N/A'}
      </DialogTitle>
      <DialogContent>
        <List>
          <ListItem sx={{ p: 0 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Note', { defaultValue: 'Note' })}
              secondary={reminder?.note || '/'}
            />
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Remind date', { defaultValue: 'Remind date' })}
              secondary={reminder?.remindDat ? getBeautifiedLocalDate(reminder?.remindDat) : '/'}
            />
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Author', { defaultValue: 'Author' })}
              secondary={reminder?.author || '/'}
            />
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Event type', { defaultValue: 'Event type' })}
              secondary={reminder?.eventType || '/'}
            />
          </ListItem>
        </List>
        <Box
          component="form"
          onSubmit={form.handleSubmit(baseSubmit)}
          noValidate
          sx={{ mt: '1rem' }}
        >
          <Controller
            control={form.control}
            name="endType"
            render={({ field }) => (
              <TextField
                {...field}
                label={t('End type', { defaultValue: 'End type' })}
                fullWidth
                error={!!form.formState.errors.endType}
                helperText={form.formState.errors.endType?.message?.toString()}
              />
            )}
          />

          <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>

          <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>

          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewReminderModal;
